// P3 concept knowledge base for DevAIService's deterministic explanations.
// Each entry is matched against the learner's prompt by keyword. This is
// intentionally a small, curated set tied to the real seeded episodes —
// not a general-purpose knowledge base — so every answer can honestly
// relate back to a CyberTales story (P3 product principle: reinforce the
// lesson, don't replace it).
export interface SarahConcept {
  topic: string
  keywords: string[]
  explanation: string
  relatedEpisodeId: string
  relatedEpisodeTitle: string
}

export const sarahConcepts: SarahConcept[] = [
  {
    topic: 'phishing',
    keywords: ['phishing', 'phish', 'fake email', 'suspicious link', 'suspicious email'],
    explanation:
      "Phishing is when someone impersonates a trusted source — a bank, a company, even a coworker — to trick you into clicking a link, entering your details, or sending money. The biggest tell is urgency: messages that pressure you to act *right now* before you have time to think it through.",
    relatedEpisodeId: 'ep-phishing-trap',
    relatedEpisodeTitle: 'The Phishing Trap',
  },
  {
    topic: 'sim-swap',
    keywords: ['sim swap', 'sim-swap', 'simswap', 'lost signal', 'no service', 'mobile money fraud'],
    explanation:
      "A SIM swap is when an attacker convinces your mobile carrier to move your phone number onto a SIM card they control — usually using details they've gathered about you. Once they have your number, they can intercept the SMS codes your bank and mobile money apps use to verify you, and take over those accounts.",
    relatedEpisodeId: 'ep-simswap-shadows',
    relatedEpisodeTitle: 'SIM Swap Shadows',
  },
  {
    topic: 'passwords',
    keywords: ['password', 'passwords', 'mfa', 'multi-factor', '2fa', 'two-factor', 'account security'],
    explanation:
      "A strong password is long, unique to each account, and not reused anywhere else — reuse is the real danger, because one leaked password can unlock every account it's used on. Multi-factor authentication (MFA) adds a second step, like a code from your phone, so a stolen password alone isn't enough to get in.",
    relatedEpisodeId: 'ep-password-fortress',
    relatedEpisodeTitle: 'Password Fortress',
  },
  {
    topic: 'social-engineering',
    keywords: ['social engineering', 'manipulat', 'pretexting', 'impersonat'],
    explanation:
      'Social engineering is the umbrella term for tricking people rather than hacking systems — using urgency, trust, or authority to get someone to do something risky, like clicking a link or sharing a code. Phishing and SIM-swap fraud are both forms of social engineering.',
    relatedEpisodeId: 'ep-phishing-trap',
    relatedEpisodeTitle: 'The Phishing Trap',
  },
]

export function findConceptByPrompt(prompt: string): SarahConcept | null {
  const lower = prompt.toLowerCase()
  return sarahConcepts.find((c) => c.keywords.some((k) => lower.includes(k))) ?? null
}
